import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getContactCreatableRoles,
  getCreatableRoles,
  hasAdminAccess,
} from '../utils/userPermissions';
import { canAccessUsersMenu, isOrgAdmin, isLHPHMember, isSuperAdmin } from '../utils';
import { ADMIN_ROLE_IDS } from '../enums/UserRoleId.enum';

interface AdminRouteGuardProps {
  children: React.ReactNode;
  requireUserCreation?: boolean; // Whether this route requires user creation permission
  requireContactCreation?: boolean; // Dealer contact screens: a contact is not a login, so dealer admins keep them
  isUsersRoute?: boolean; // Whether this is the Users route
  requireOrgAdmin?: boolean; // Whether this route requires org admin access (SuperAdmin, Admin, Dealer Admin)
  requireLHPHAdmin?: boolean; // Whether this route requires LHPH admin access (SuperAdmin, Admin, LHPH Admin)
  requireLHPHAccess?: boolean; // Whether this route requires LHPH member or above (SuperAdmin, Admin, LHPHAdmin, LHPHUser)
  requireSuperAdmin?: boolean; // Whether this route requires SuperAdmin only
}

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({
  children,
  requireUserCreation = false,
  requireContactCreation = false,
  isUsersRoute = false,
  requireOrgAdmin = false,
  requireLHPHAdmin = false,
  requireLHPHAccess = false,
  requireSuperAdmin = false,
}) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  // For routes requiring SuperAdmin only (e.g. bug-reports admin page)
  if (requireSuperAdmin) {
    if (!isSuperAdmin(user)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // For routes requiring LHPH access (SuperAdmin, Admin, LHPHAdmin, LHPHUser)
  // Intentionally excludes DealerAdmin — isOrgAdmin includes DealerAdmin (5),
  // which the comment above explicitly excludes.
  if (requireLHPHAccess) {
    const allowed = !!user.role_id && (ADMIN_ROLE_IDS.has(user.role_id) || isLHPHMember(user));
    if (!allowed) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // For routes requiring LHPH admin access (SuperAdmin, Admin, LHPH Admin)
  if (requireLHPHAdmin) {
    if (!user.role_id || !ADMIN_ROLE_IDS.has(user.role_id)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // For routes requiring org admin access
  if (requireOrgAdmin) {
    if (!isOrgAdmin(user)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // For Users route, check if user can access users menu
  if (isUsersRoute) {
    if (!canAccessUsersMenu(user)) {
      return <Navigate to="/unauthorized" replace />;
    }
  } else if (!requireOrgAdmin) {
    // For other admin routes, check if user has admin-like permissions
    if (!user.role_id) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // If this route requires user creation permission, check if user can create any users
  if (requireUserCreation && user.role_id) {
    const creatableRoles = getCreatableRoles(user.role_id);
    if (creatableRoles.length === 0) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (requireContactCreation && user.role_id) {
    if (getContactCreatableRoles(user.role_id).length === 0) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default AdminRouteGuard;
